import React, {useEffect, useState} from 'react'
import {createJobPost, getJobPosts} from '../../../api/HiringAPI'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '../../modules/auth'
import {Select} from '@mantine/core'
import {getLocations} from '../../../api/LocationAPI'
import {getDepartments} from '../../../api/DepartmentAPI'

type HiringLocation = { id: string; name: string; city?: string | null; isActive: boolean }
type HiringDepartment = { id: string; name: string; isActive: boolean }
const OTHER_LOCATION_VALUE = '__OTHER_LOCATION__'

const getExperienceErrors = (minimum: string, maximum: string) => {
  const errors: {[key: string]: string} = {}
  const minValue = minimum.trim() === '' ? undefined : Number(minimum)
  const maxValue = maximum.trim() === '' ? undefined : Number(maximum)

  if (minValue !== undefined && (!Number.isFinite(minValue) || minValue < 0)) {
    errors.minExperience = 'Minimum experience must be a non-negative number.'
  }
  if (maxValue !== undefined && (!Number.isFinite(maxValue) || maxValue < 0)) {
    errors.maxExperience = 'Maximum experience must be a non-negative number.'
  }
  if (minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
    errors.minExperience = 'Minimum experience must be less than or equal to maximum experience.'
  }
  return errors
}

const getStoredSkills = (skills: unknown): string[] => {
  const values = Array.isArray(skills)
    ? skills
    : typeof skills === 'string'
      ? skills.split(',')
      : []
  return values.map((skill) => String(skill).trim()).filter(Boolean)
}

const CreateJobPost: React.FC = () => {
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const [form, setForm] = useState<any>({
    title: '',
    department: '',
    departmentId: '',
    description: '',
    location: '',
    locationId: '',
    workMode: 'ONSITE',
    employmentType: 'FULL_TIME',
    openings: 1,
    minExperience: '',
    maxExperience: '',
    skills: [] as string[],
    compensationMin: '',
    compensationMax: '',
    currency: 'INR',
    joiningTimeline: '',
    qualification: '',
    benefits: '',
    jobCode: '',
    applicationDeadline: '',
    visibility: 'INTERNAL',
    status: 'OPEN',
    isPublished: true,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldError, setFieldError] = useState<{[k:string]: string}>({})
  const [skillInput, setSkillInput] = useState('')
  const [skillOptions, setSkillOptions] = useState<string[]>([])
  const [skillOptionsLoading, setSkillOptionsLoading] = useState(false)
  const [skillOptionsError, setSkillOptionsError] = useState('')
  const [departments, setDepartments] = useState<HiringDepartment[]>([])
  const [departmentsLoading, setDepartmentsLoading] = useState(false)
  const [departmentsError, setDepartmentsError] = useState('')
  const [locations, setLocations] = useState<HiringLocation[]>([])
  const [locationsLoading, setLocationsLoading] = useState(false)
  const [locationsError, setLocationsError] = useState('')
  const isOtherLocation = form.locationId === OTHER_LOCATION_VALUE

  useEffect(() => {
    let active = true
    const loadDepartments = async () => {
      setDepartmentsLoading(true)
      setDepartmentsError('')
      try {
        const response: any = await getDepartments()
        const records = Array.isArray(response?.data) ? response.data : Array.isArray(response?.data?.data) ? response.data.data : []
        if (response?.success === false || (response?.statusCode && response.statusCode >= 400)) {
          throw new Error(response?.message || 'Departments could not be loaded.')
        }
        if (active) setDepartments(records.filter((department: HiringDepartment) => department.isActive !== false))
      } catch (loadError: any) {
        if (active) {
          setDepartments([])
          setDepartmentsError(loadError?.message || 'Departments could not be loaded. Please try again.')
        }
      } finally {
        if (active) setDepartmentsLoading(false)
      }
    }
    loadDepartments()
    return () => { active = false }
  }, [])

  useEffect(() => {
    let active = true
    const loadLocations = async () => {
      setLocationsLoading(true)
      setLocationsError('')
      try {
        const response: any = await getLocations(true)
        if (response?.statusCode !== 200 || !Array.isArray(response?.data)) {
          throw new Error(response?.message || 'Active locations could not be loaded.')
        }
        if (active) setLocations(response.data.filter((location: HiringLocation) => location.isActive))
      } catch (loadError: any) {
        if (active) {
          setLocations([])
          setLocationsError(loadError?.response?.data?.message || loadError?.message || 'Locations could not be loaded. Please try again.')
        }
      } finally {
        if (active) setLocationsLoading(false)
      }
    }
    loadLocations()
    return () => { active = false }
  }, [])

  useEffect(() => {
    let active = true
    const loadSkillOptions = async () => {
      setSkillOptionsLoading(true)
      setSkillOptionsError('')
      try {
        const response = await getJobPosts({ limit: 100, offset: 0 })
        const records = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : []
        const skills: string[] = records.flatMap((job: any): string[] =>
          getStoredSkills(job.skillsRequired)
        )
        const uniqueSkills = Array.from(
          new Map<string, string>(
            skills.map((skill) => [skill.toLowerCase(), skill])
          ).values()
        )
        if (active) setSkillOptions(uniqueSkills)
      } catch (loadError) {
        if (active) setSkillOptionsError('Existing skills could not be loaded. You can still add skills manually.')
      } finally {
        if (active) setSkillOptionsLoading(false)
      }
    }
    loadSkillOptions()
    return () => { active = false }
  }, [])

  const addSkill = (value: string) => {
    const skill = value.trim().replace(/,+$/, '').trim()
    if (!skill) return
    if (form.skills.some((selected: string) => selected.toLowerCase() === skill.toLowerCase())) {
      setSkillInput('')
      return
    }
    setForm((previous: any) => ({ ...previous, skills: [...previous.skills, skill] }))
    setSkillOptions((previous) => previous.some((option) => option.toLowerCase() === skill.toLowerCase())
      ? previous
      : [...previous, skill])
    setSkillInput('')
  }

  const handleSubmit = async () => {
    setError('')
    const nextErrors: {[k:string]: string} = {}
    if (!form.title.trim()) nextErrors.title = 'Title is required'
    if (!form.departmentId) nextErrors.department = 'Please select an active department'
    if (!form.description.trim()) nextErrors.description = 'Description is required'
    Object.assign(nextErrors, getExperienceErrors(form.minExperience, form.maxExperience))
    setFieldError(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    if (!currentUser?.id) {
      setError('Missing current user. Please re-login and try again.')
      return
    }
    setLoading(true)
    const payload = {
      title: form.title,
      department: form.department,
      departmentId: form.departmentId,
      description: form.description,
      location: form.location.trim() || undefined,
      locationId: form.locationId && !isOtherLocation ? form.locationId : undefined,
      workMode: form.workMode,
      openings: Number(form.openings || 1),
      minExperience: form.minExperience !== '' ? Number(form.minExperience) : undefined,
      maxExperience: form.maxExperience !== '' ? Number(form.maxExperience) : undefined,
      skillsRequired: form.skills.length ? form.skills : undefined,
      employmentType: form.employmentType,
      compensationMin: form.compensationMin !== '' ? Number(form.compensationMin) : undefined,
      compensationMax: form.compensationMax !== '' ? Number(form.compensationMax) : undefined,
      currency: form.currency || 'INR',
      joiningTimeline: form.joiningTimeline || undefined,
      qualification: form.qualification || undefined,
      benefits: form.benefits || undefined,
      jobCode: form.jobCode || undefined,
      applicationDeadline: form.applicationDeadline ? new Date(form.applicationDeadline) : undefined,
      isPublished: form.status === 'ON_HOLD' ? false : !!form.isPublished,
      visibility: form.visibility,
      status: form.status,
      postedById: String(currentUser.id),
    }
    try {
      const res = await createJobPost(payload)
      if (res?.id || res?.data?.id || res?.statusCode === 200 || res?.statusCode === 201) {
        navigate('/hiring/jobs')
      } else {
        setError(res?.message || res?.error || 'Failed to create job post')
      }
    } catch (submitError: any) {
      setError(submitError?.message || 'Failed to create job post')
    } finally {
      setLoading(false)
    }
  }

  const matchingSkills = skillOptions
    .filter((skill) => skill.toLowerCase().includes(skillInput.trim().toLowerCase()))
    .filter((skill) => !form.skills.some((selected: string) => selected.toLowerCase() === skill.toLowerCase()))
    .slice(0, 8)
  const canAddSkillInput = skillInput.trim().length > 0 && !form.skills.some(
    (selected: string) => selected.toLowerCase() === skillInput.trim().toLowerCase()
  )

  return (
    <div className='card'>
      <div className='card-header'><h3 className='card-title fw-bold mb-0'>Create Job Post</h3></div>
      <div className='card-body'>
        {error && <div className='alert alert-danger'>{error}</div>}
        <div className='row g-5'>
          <div className='col-md-6'>
            <label className='form-label' htmlFor='job-title'>Title <span className='text-danger' aria-hidden='true'>*</span></label>
            <input id='job-title' aria-required='true' aria-invalid={Boolean(fieldError.title)} aria-describedby={fieldError.title ? 'job-title-error' : undefined} className={`form-control ${fieldError.title ? 'is-invalid' : ''}`} value={form.title} onChange={(e)=>{ setForm({...form, title:e.target.value}); if (e.target.value.trim()) setFieldError(prev=>({ ...prev, title: '' })) }} />
            {fieldError.title && <div id='job-title-error' className='invalid-feedback' role='alert'>{fieldError.title}</div>}
          </div>
          <div className='col-md-6'>
            <label className='form-label' htmlFor='job-department'>Department <span className='text-danger' aria-hidden='true'>*</span></label>
            <Select
              id='job-department'
              aria-label='Department'
              searchable
              nothingFound='No active departments found'
              placeholder={departmentsLoading ? 'Loading departments...' : 'Select a department'}
              data={departments.map((department) => ({ value: department.id, label: department.name }))}
              value={form.departmentId || null}
              disabled={departmentsLoading || Boolean(departmentsError) || departments.length === 0}
              error={fieldError.department}
              onChange={(departmentId) => {
                const selected = departments.find((department) => department.id === departmentId)
                setForm({...form, departmentId: departmentId || '', department: selected?.name || ''})
                if (departmentId) setFieldError((previous) => ({ ...previous, department: '' }))
              }}
              styles={{
                root: { width: '100%' },
                wrapper: { height: '38px' },
                input: { height: '38px', minHeight: '38px', padding: '0.375rem 0.75rem' },
                dropdown: { maxHeight: 200 }
              }}
            />
            {departmentsError && <div id='job-department-error' className='text-danger mt-1' role='alert'>{departmentsError}</div>}
            {!departmentsLoading && !departmentsError && departments.length === 0 && <div className='text-muted mt-1'>No active departments are available.</div>}
            {fieldError.department && <div id='job-department-error' className='invalid-feedback' role='alert'>{fieldError.department}</div>}
          </div>
          <div className='col-md-12'>
            <label className='form-label' htmlFor='job-description'>Description <span className='text-danger' aria-hidden='true'>*</span></label>
            <textarea id='job-description' aria-required='true' aria-invalid={Boolean(fieldError.description)} aria-describedby={fieldError.description ? 'job-description-error' : undefined} className={`form-control ${fieldError.description ? 'is-invalid' : ''}`} rows={6} value={form.description} onChange={(e)=>{ setForm({...form, description:e.target.value}); if (e.target.value.trim()) setFieldError(prev=>({ ...prev, description: '' })) }} />
            {fieldError.description && <div id='job-description-error' className='invalid-feedback' role='alert'>{fieldError.description}</div>}
          </div>
          <div className='col-md-4'>
            <label className='form-label' htmlFor='job-location'>Location</label>
            <Select
              id='job-location'
              size='sm'
              searchable
              clearable
              placeholder={locationsLoading ? 'Loading active locations...' : 'Search and select a location'}
              data={[
                ...locations.map((location) => ({ value: location.id, label: `${location.name}${location.city ? ` - ${location.city}` : ''}` })),
                { value: OTHER_LOCATION_VALUE, label: 'Other' },
              ]}
              value={form.locationId || null}
              onChange={(locationId) => {
                const selected = locations.find((location) => location.id === locationId)
                setForm((previous: any) => ({
                  ...previous,
                  locationId: locationId || '',
                  location: locationId === OTHER_LOCATION_VALUE ? '' : selected?.name || '',
                }))
              }}
              disabled={locationsLoading}
              styles={{ root: { width: '100%' }, wrapper: { height: '38px' }, input: { height: '38px' }, dropdown: { maxHeight: 200 } }}
              nothingFound='No matching active locations'
            />
            {locationsError && <div className='form-text text-danger' role='alert'>{locationsError}</div>}
            {!locationsError && !locationsLoading && locations.length === 0 && <div className='form-text'>No active locations are available.</div>}
            {isOtherLocation && (
              <input
                className='form-control mt-2'
                aria-label='Enter location'
                placeholder='Enter location'
                value={form.location}
                onChange={(event) => setForm((previous: any) => ({ ...previous, location: event.target.value }))}
              />
            )}
          </div>
          <div className='col-md-4'>
            <label className='form-label'>Work Mode</label>
            <select className='form-select' value={form.workMode} onChange={(e)=>setForm({...form, workMode:e.target.value})}>
              <option value='ONSITE'>Onsite</option>
              <option value='REMOTE'>Remote</option>
              <option value='HYBRID'>Hybrid</option>
            </select>
          </div>
          <div className='col-md-4'>
            <label className='form-label'>Employment Type</label>
            <select className='form-select' value={form.employmentType} onChange={(e)=>setForm({...form, employmentType:e.target.value})}>
              <option value='FULL_TIME'>Full-time</option>
              <option value='PART_TIME'>Part-time</option>
              <option value='CONTRACT'>Contract</option>
              <option value='INTERN'>Intern</option>
              <option value='FREELANCE'>Freelance</option>
            </select>
          </div>
          <div className='col-md-4'>
            <label className='form-label'>Openings</label>
            <input type='number' min={1} className='form-control' value={form.openings} onChange={(e)=>setForm({...form, openings:e.target.value})} />
          </div>
          <div className='col-md-4'>
            <label className='form-label' htmlFor='min-experience'>Min Experience (yrs)</label>
            <input id='min-experience' type='number' min='0' step='any' inputMode='decimal' aria-invalid={Boolean(fieldError.minExperience)} aria-describedby={fieldError.minExperience ? 'min-experience-error' : undefined} className={`form-control ${fieldError.minExperience ? 'is-invalid' : ''}`} value={form.minExperience} onChange={(e)=>{
              const minExperience = e.target.value
              const errors = getExperienceErrors(minExperience, form.maxExperience)
              setForm({...form, minExperience})
              setFieldError((previous) => {
                const next = {...previous}
                delete next.minExperience
                delete next.maxExperience
                return {...next, ...errors}
              })
            }} />
            {fieldError.minExperience && <div id='min-experience-error' className='invalid-feedback' role='alert'>{fieldError.minExperience}</div>}
          </div>
          <div className='col-md-4'>
            <label className='form-label' htmlFor='max-experience'>Max Experience (yrs)</label>
            <input id='max-experience' type='number' min='0' step='any' inputMode='decimal' aria-invalid={Boolean(fieldError.maxExperience)} aria-describedby={fieldError.maxExperience ? 'max-experience-error' : undefined} className={`form-control ${fieldError.maxExperience ? 'is-invalid' : ''}`} value={form.maxExperience} onChange={(e)=>{
              const maxExperience = e.target.value
              const errors = getExperienceErrors(form.minExperience, maxExperience)
              setForm({...form, maxExperience})
              setFieldError((previous) => {
                const next = {...previous}
                delete next.minExperience
                delete next.maxExperience
                return {...next, ...errors}
              })
            }} />
            {fieldError.maxExperience && <div id='max-experience-error' className='invalid-feedback' role='alert'>{fieldError.maxExperience}</div>}
          </div>
          <div className='col-md-12'>
            <label className='form-label' htmlFor='job-skills'>Skills</label>
            <div className='position-relative'>
              <div className='form-control d-flex flex-wrap align-items-center gap-2' style={{ minHeight: '42px', height: 'auto' }}>
                {form.skills.map((skill: string) => (
                  <span key={skill.toLowerCase()} className='badge badge-light-primary d-inline-flex align-items-center gap-2'>
                    {skill}
                    <button
                      type='button'
                      className='btn btn-sm btn-icon p-0'
                      aria-label={`Remove ${skill}`}
                      onClick={() => setForm((previous: any) => ({
                        ...previous,
                        skills: previous.skills.filter((selected: string) => selected.toLowerCase() !== skill.toLowerCase()),
                      }))}
                    >
                      <i className='bi bi-x' aria-hidden='true' />
                    </button>
                  </span>
                ))}
                <input
                  id='job-skills'
                  className='border-0 flex-grow-1'
                  style={{ minWidth: '180px', outline: 'none' }}
                  value={skillInput}
                  placeholder='Search or type a skill, then press Enter'
                  role='combobox'
                  aria-autocomplete='list'
                  aria-expanded={Boolean(skillInput.trim())}
                  aria-controls='job-skill-suggestions'
                  onChange={(event) => {
                    if (event.target.value.includes(',')) addSkill(event.target.value)
                    else setSkillInput(event.target.value)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ',') {
                      event.preventDefault()
                      addSkill(skillInput)
                    } else if (event.key === 'Backspace' && !skillInput && form.skills.length) {
                      setForm((previous: any) => ({ ...previous, skills: previous.skills.slice(0, -1) }))
                    }
                  }}
                />
              </div>
              {skillInput.trim() && (
                <div id='job-skill-suggestions' className='dropdown-menu show w-100 mt-1' role='listbox'>
                  {skillOptionsLoading ? (
                    <span className='dropdown-item-text text-muted' role='status'>Loading existing skills...</span>
                  ) : (
                    <>
                      {matchingSkills.map((skill) => (
                        <button key={skill.toLowerCase()} type='button' className='dropdown-item' role='option' onClick={() => addSkill(skill)}>
                          {skill}
                        </button>
                      ))}
                      {canAddSkillInput && (
                        <button type='button' className='dropdown-item text-primary' role='option' onClick={() => addSkill(skillInput)}>
                          Add “{skillInput.trim()}”
                        </button>
                      )}
                      {!matchingSkills.length && !canAddSkillInput && (
                        <span className='dropdown-item-text text-muted'>No matching skills</span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className='form-text'>Select an existing skill or type a new one. Duplicate skills are ignored.</div>
            {skillOptionsError && <div className='form-text text-warning' role='status'>{skillOptionsError}</div>}
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Compensation Min</label>
            <input type='number' className='form-control' value={form.compensationMin} onChange={(e)=>setForm({...form, compensationMin:e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Compensation Max</label>
            <input type='number' className='form-control' value={form.compensationMax} onChange={(e)=>setForm({...form, compensationMax:e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Currency</label>
            <input className='form-control' value={form.currency} onChange={(e)=>setForm({...form, currency:e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Application Deadline</label>
            <input type='date' className='form-control' value={form.applicationDeadline} onChange={(e)=>setForm({...form, applicationDeadline:e.target.value})} />
          </div>
          <div className='col-md-6'>
            <label className='form-label'>Joining Timeline</label>
            <input className='form-control' value={form.joiningTimeline} onChange={(e)=>setForm({...form, joiningTimeline:e.target.value})} />
          </div>
          <div className='col-md-6'>
            <label className='form-label'>Qualification</label>
            <input className='form-control' value={form.qualification} onChange={(e)=>setForm({...form, qualification:e.target.value})} />
          </div>
          <div className='col-md-6'>
            <label className='form-label'>Benefits</label>
            <input className='form-control' value={form.benefits} onChange={(e)=>setForm({...form, benefits:e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Job Code</label>
            <input className='form-control' value={form.jobCode} onChange={(e)=>setForm({...form, jobCode:e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Visibility</label>
            <select className='form-select' value={form.visibility} onChange={(e)=>setForm({...form, visibility:e.target.value})}>
              <option value='INTERNAL'>Internal</option>
              <option value='PUBLIC'>Public</option>
              <option value='AGENCY'>Agency</option>
            </select>
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Status</label>
            <select className='form-select' value={form.status} onChange={(e)=>setForm((previous: any)=>({
              ...previous,
              status: e.target.value,
              ...(e.target.value === 'ON_HOLD' ? { isPublished: false } : {}),
            }))}>
              <option value='OPEN'>Open</option>
              <option value='CLOSED'>Closed</option>
              <option value='ON_HOLD'>On Hold</option>
            </select>
            <div className='form-text'>On Hold pauses this job listing. Candidate application status is managed separately.</div>
          </div>
          <div className='col-md-3 d-flex align-items-end'>
            <div className='form-check form-check-custom form-check-solid'>
              <input className='form-check-input' type='checkbox' id='isPublished' checked={form.status === 'ON_HOLD' ? false : form.isPublished} disabled={form.status === 'ON_HOLD'} onChange={(e)=>setForm({...form, isPublished: e.target.checked})} />
              <label className='form-check-label' htmlFor='isPublished'>Published</label>
            </div>
          </div>
          <div className='col-12'>
            <div className='form-text'>When enabled, candidates can view and apply to this job while its status is Open.</div>
          </div>
        </div>
      </div>
      <div className='card-footer d-flex justify-content-between'>
        <button className='btn btn-light' onClick={() => navigate('/hiring/jobs')}>Cancel</button>
        <button className='btn btn-primary' disabled={loading} onClick={handleSubmit}>Create JD</button>
      </div>
    </div>
  )
}

export default CreateJobPost


